/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Toast from 'react-native-toast-message';

import csvTemplate from '@/assets/csv/import-outlet-template';
import csvTemplateActiveInactive from '@/assets/csv/import-active-inactive-template';
import csvTaxTemplate from '@/assets/csv/import-tax-outlet-template';
import SociollaText from '@components/sociolla/SociollaText';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import * as Layout from '@components/screen/Layout';
import Table from '@components/table/Table';
import StatusBadge from '@components/global/StatusBadge';
import TableMobile from '@components/table/TableMobile';
import Image from '@components/global/Image';
import Button from '@components/form/Button';
import ModalUpload from '@components/modal/ModalUpload';

import { truncateString, hasNoAccess } from '@global/helpers';
import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import { showPopup } from '@/store/modal';
import { MsAccount } from '@api/ms-account';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetSubdistributorListQuery,
  useGetSubdistributorCountQuery,
  useGetSubdistributorInfiniteListInfiniteQuery,
} from '@api/listApi';

const tableStructure = {
  subdistributor: {
    head: 'Sub-Distributor Name',
    body: 'customer.name',
  },
  name: {
    head: 'Outlet Name',
    body: 'name',
  },
  code: {
    head: 'Outlet Code',
    body: 'code',
  },
  sales_team: {
    head: 'Sales Team',
    body: 'sales_team.name',
  },
  market_type: {
    head: 'Market Type',
    body: 'market_type.code',
  },
  business_type: {
    head: 'Business Type',
    body: 'business_type',
    type: 'slot',
  },
  is_active: {
    head: 'Is Active',
    body: 'is_active',
    type: 'status_active',
  },
};

const searchForOptions = [
  {
    label: 'Sub-Distributor Name',
    value: 'customer.name',
  },
  {
    label: 'Outlet Name',
    value: 'name',
  },
  {
    label: 'Outlet Code',
    value: 'code',
  },
];

const img = require('@/assets/img/default/image-default.png');

// Search-only list (no filter modal). The search box writes `keyword` (+ the
// `search_for` field selector); this transform unwraps them into the BE
// `keyword` / `keyword_for` query the old finalParam produced.
const toApiFilter = (rawFilter = {}) => {
  const filter = {};
  const stored = rawFilter.keyword;
  const kw = stored?.$regex ?? stored;
  if (kw) {
    filter.keyword = kw;
    const sf = rawFilter.search_for;
    if (sf) {
      filter.keyword_for = sf?.value ?? sf;
    }
  }
  return filter;
};

function SubdistributorScreen(props) {
  const dispatch = useDispatch();
  const showOutletImport = useSelector((state) => state.modal.showOutletImport);
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [importOption, setImportOption] = useState('');

  // ── list data ──
  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetSubdistributorListQuery,
    countHook: useGetSubdistributorCountQuery,
    infiniteHook: useGetSubdistributorInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  // ── derived ──
  const templateUrlMap = {
    outlet: csvTemplate,
    outlet_active_inactive: csvTemplateActiveInactive,
    outlet_tax: csvTaxTemplate,
  };
  const templateNameMap = {
    outlet: 'import-outlet-template',
    outlet_active_inactive: 'import-active-inactive-template',
    outlet_tax: 'import-tax-template',
  };
  const templateHeaderMap = {
    outlet: [
      'subdistributor_code',
      'outlet_name',
      'outlet_code',
      'outlet_sales_team',
      'outlet_market_type',
      'outlet_business_type',
      'outlet_country',
      'outlet_state',
      'outlet_city',
      'outlet_street',
      'tax_name',
      'tax_number',
      'pkp',
      'wht',
      'is_active',
    ],
    outlet_active_inactive: ['outlet_code', 'is_active'],
    outlet_tax: ['outlet_code', 'tax_name', 'tax_number', 'pkp', 'wht'],
  };

  const hasAccessExport = !hasNoAccess([
    'superadmin',
    'general',
    'branch_head',
    'regional_manager',
    'regional_support',
    'key_account_manager',
    'group_key_account_manager',
    'national_key_account_manager',
    'distributor_manager',
    'general_manager',
    'vice_president',
    'finance_supervisor',
    'vice_president_finance',
    'budget_controller',
    'finance_head',
    'tax_support',
    'billing_supervisor',
    'general_manager_marketing',
    'marketing_admin',
    'marketing_support',
    'chief_of_staff',
    'regional_distributor_manager',
  ]);

  // ── memoized values ──
  // Current search text / field, derived from the applied filter.
  const searchValue = useMemo(() => {
    const k = params.filter?.keyword;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);
  const searchForValue = useMemo(() => {
    return (
      params.filter?.search_for || {
        label: 'Sub-Distributor Name',
        value: 'customer.name',
      }
    );
  }, [params.filter]);

  // Web = flat array; mobile = paged infinite result. id is already mapped by the factory.
  const rows = useMemo(() => {
    return isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
  }, [list.data]);

  // ── effects ──
  useEffect(() => {
    try {
      const menus = [];
      if (hasAccessExport) {
        menus.push({
          type: 'function',
          name: 'Export',
          callback: exportSubdis,
        });
      }
      menus.push({
        type: 'function',
        name: 'Import Outlet',
        callback: () => {
          handleImportOption('outlet');
        },
      });
      menus.push({
        type: 'function',
        name: 'Import Active/Inactive',
        callback: () => {
          handleImportOption('outlet_active_inactive');
        },
      });
      menus.push({
        type: 'function',
        name: 'Import Tax Identification',
        callback: () => {
          handleImportOption('outlet_tax');
        },
      });
      props.navigation.setOptions({
        headerRight: () => {
          return isWeb ? (
            <NotificationSettingButton {...props} />
          ) : (
            <MoreButton menu={menus} {...props} />
          );
        },
      });
    } catch (error) {}
  }, [props.navigation]);

  // ── handlers ──
  const gotToDetail = (data) => {
    openChild('SubdistributorDetailScreen', {
      titleScreen: data.customer.name,
      name: data.customer.name,
      id: data._id,
      go_back_key: props.route.key,
    });
  };

  const exportSubdis = async () => {
    await MsAccount.exportOutlet();
    Toast.show({
      text1: 'Export in progress, the result will be sent to your email',
      type: 'success',
    });
  };

  const combineMarketBusType = (item) => {
    return `${item.market_type?.code} | ${truncateString(
      item.business_type?.code,
      13,
    )}`;
  };

  const onImport = async (csvUrl) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const response = await MsAccount.createBulkCustomer({
        url: csvUrl,
        type: importOption,
      });

      const isSuccess = response?.status === 200;

      Toast.show({
        text1: isSuccess ? 'Import file successfully' : 'Import file failed',
        type: isSuccess ? 'success' : 'error',
      });
    } catch (error) {
      Toast.show({ text1: 'Import file failed', type: 'error' });
    } finally {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      dispatch(
        showPopup({
          name: 'showOutletImport',
          value: false,
        }),
      );
    }
  };

  const handleImportOption = (opt) => {
    setImportOption(opt);
    setShowImportOptions(false);
    dispatch(
      showPopup({
        name: 'showOutletImport',
        value: true,
      }),
    );
  };

  // ── render ──
  return (
    <Layout.Container>
      {isWeb && (
        <Layout.Title title="Sub-Distributor" style={{ zIndex: 9 }}>
          {hasAccessExport ? (
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
              }}>
              <View style={{ flexDirection: 'row' }}>
                <Button
                  type="default"
                  rounded={true}
                  buttonStyle={{ width: 110, marginRight: 10 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.black }}>
                      Import
                    </SociollaText>
                  }
                  onPress={() => {
                    setShowImportOptions((prevState) => !prevState);
                  }}
                />
                {showImportOptions ? (
                  <View style={styles.addOptions}>
                    <TouchableOpacity
                      activeOpacity={0.8}
                      style={styles.addOptionsItem}
                      onPress={() => handleImportOption('outlet')}>
                      <SociollaText>Import Outlet Subdist</SociollaText>
                    </TouchableOpacity>
                    <TouchableOpacity
                      activeOpacity={0.8}
                      style={styles.addOptionsItem}
                      onPress={() =>
                        handleImportOption('outlet_active_inactive')
                      }>
                      <SociollaText>Import Active / Inactive</SociollaText>
                    </TouchableOpacity>
                    <TouchableOpacity
                      activeOpacity={0.8}
                      style={styles.addOptionsItem}
                      onPress={() => handleImportOption('outlet_tax')}>
                      <SociollaText>Import Tax Identification</SociollaText>
                    </TouchableOpacity>
                  </View>
                ) : (
                  false
                )}
              </View>
              <Button
                type="primary"
                rounded={true}
                buttonStyle={{ width: 90, marginRight: 10 }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.white }}>
                    Export
                  </SociollaText>
                }
                onPress={exportSubdis}
              />
            </View>
          ) : (
            false
          )}
        </Layout.Title>
      )}
      {isWeb ? (
        <Table
          filterName={scope}
          name="subdistributor"
          structures={tableStructure}
          loading={list.isLoading}
          refetching={list.isFetching}
          data={rows}
          searchKey="keyword"
          searchPlaceholder="Search by sub-distributor name, outlet name, outlet code"
          searchValue={searchValue}
          searchWidth={450}
          hasNoHide={true}
          hasCallback={true}
          totalData={count.data}
          skip={params.skip}
          limit={params.limit}
          onClickRow={({ data }) => gotToDetail(data)}
          searchForOptions={searchForOptions}
          searchForValue={searchForValue}
          showFilter={false}
          slot={{
            business_type: ({ data, index }) => (
              <SociollaText key={index} style={{ fontSize: 12 }}>{`${
                data.business_type ? data.business_type.code : '-'
              } (${
                data.business_type ? data.business_type.description : '-'
              })`}</SociollaText>
            ),
          }}
        />
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="keyword"
          placeholder="Search by sub-distributor name, outlet name, outlet code"
          searchValue={searchValue}
          searchForOptions={searchForOptions}
          searchForValue={searchForValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={rows}
          totalData={count.data}
          renderItem={({ data, index }) => (
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.subdisItem}
              onPress={() => gotToDetail(data)}
              key={index}>
              <View style={{ flexDirection: 'row' }}>
                <View
                  style={[
                    styles.subdisItemSection,
                    {
                      borderRightWidth: 2,
                      borderRightColor: Colors.grey0,
                      width: '60%',
                    },
                  ]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Image
                      style={{ width: 24, height: 24, margin: 5 }}
                      width={24}
                      height={24}
                      source={
                        data.customer.image ? { uri: data.customer.image } : img
                      }
                    />
                    <SociollaText type="medium" style={{ fontSize: 15 }}>
                      {truncateString(
                        data.customer && data.customer.name
                          ? data.customer.name
                          : '-',
                      )}
                    </SociollaText>
                  </View>
                  <SociollaText type="medium">{data.name || ''}</SociollaText>
                  <SociollaText>{data.code || ''}</SociollaText>
                </View>

                <View style={[styles.subdisItemSection, { width: '40%' }]}>
                  <StatusBadge
                    status={data.is_active ? 'active' : 'in_active'}
                  />
                  <SociollaText type="medium" style={{ marginTop: 5 }}>
                    {data.sales_team && data.sales_team.name
                      ? data.sales_team.name
                      : '-'}
                  </SociollaText>
                  <SociollaText>{combineMarketBusType(data)}</SociollaText>
                </View>
              </View>
            </TouchableOpacity>
          )}
        />
      )}

      <ModalUpload
        show={showOutletImport}
        onClose={() =>
          dispatch(
            showPopup({
              name: 'showOutletImport',
              value: false,
            }),
          )
        }
        autoUploadS3={true}
        templateUrl={templateUrlMap[importOption]}
        name={templateNameMap[importOption]}
        onUpload={onImport}
        validHeader={templateHeaderMap[importOption]}
      />
    </Layout.Container>
  );
}

const styles = StyleSheet.create({
  subdisItem: {
    width: '100%',
    paddingVertical: 20,
    paddingHorizontal: 10,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  subdisItemSection: {
    paddingHorizontal: 10,
  },
  addOptions: {
    position: 'absolute',
    width: 180,
    top: 50,
    left: -60,
    zIndex: 9,
    elevation: 1,
    backgroundColor: Colors.white,
    borderRadius: 5,
    borderColor: Colors.grey0,
    borderWidth: 1,
    ...(!isWeb
      ? {
          position: 'absolute',
          top: 0,
          left: 100,
        }
      : {}),
  },
  addOptionsItem: {
    paddingVertical: 13,
    paddingHorizontal: 15,
    ...(!isWeb
      ? {
          borderBottomColor: Colors.grey0,
          borderBottomWidth: 1,
        }
      : {}),
  },
});

export default SubdistributorScreen;
